import { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import DemoForm from "@/components/site/DemoForm";

const DemoContext = createContext({ open: () => {} });

export function useDemo() {
  return useContext(DemoContext);
}

export default function DemoModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      first?.focus?.();
    }, 40);

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = [
        ...panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  return (
    <DemoContext.Provider value={{ open: () => setOpen(true), close: () => setOpen(false), isOpen: open }}>
      {children}
      <AnimatePresence>
        {open ? (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              aria-label="Close dialog"
              onClick={() => setOpen(false)}
            />
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="relative max-h-[92vh] overflow-y-auto rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--bg-elevated)] p-6 shadow-[var(--shadow)] backdrop-blur-2xl sm:max-w-2xl sm:p-8"
                data-testid="demo-modal"
              >
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="absolute right-4 top-4 rounded-lg border border-white/10 p-2 text-slate-400 transition-colors hover:bg-white/[0.05] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
                  aria-label="Close"
                  data-testid="demo-modal-close"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="eyebrow-label mb-2">Request a demo</p>
                <h2 id={titleId} className="font-display text-2xl font-semibold tracking-tight text-slate-50 sm:text-3xl">
                  See RORAQ on your payroll data.
                </h2>
                <p className="mt-1 pt-1 text-sm text-slate-400">
                  Tell us about your environment and we will tailor the walkthrough - month-end packs, workforce
                  intelligence, automation and compliance around your payroll platform.
                </p>
                <div className="mt-6">
                  <DemoForm source="modal" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </DemoContext.Provider>
  );
}
